import {
  Form,
  useCurrentBlock,
  useFormAnswers,
} from "@quillforms/renderer-core";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import formSubmissionClient from "../lib/requests";
import "@quillforms/renderer-core/build-style/style.css";
import { registerCoreBlocks } from "@quillforms/react-renderer-utils";
import "../components/quillforms/CustomFileUploadBlock";
import "../components/quillforms/CustomPhoneNumberBlock";
import "../components/quillforms/CustomSignatureBlock";
import formBlocks, {
  ANSWER_ORDER,
  HIDDEN_BLOCK_ANSWERS,
  SUBMITTED_BLOCK_NAMES,
} from "../forms/formBlocks";
import {
  createBeforeGoingNextValidator,
  validateBirthDate,
  validateEmail,
  VALIDATION_ERRORS,
} from "../utils/validations"; // ✅ single validation function
import LiveLogger from "../utils/LiveLogger"; // ✅ fixed relative path
import { getPracticeName } from "../utils/formParams";
import { markFormOpened } from "../utils/formOpened";
import "../styles.css"; // ✅ fixed relative path
import {
  trackFormLoaded,
  trackFormStarted,
  trackWelcomeStartClicked,
  trackFormSubmissionStarted,
  trackFormSubmissionSuccess,
  trackFormSubmissionFailed,
  trackFormSubmitClicked,
  trackBlockViewed,
  trackBlockAnswered,
  trackBlockAnswerChanged,
  trackFormProgress,
} from "../utils/rum-tracking";

registerCoreBlocks();

function toCssString(text: string): string {
  return `"${text.replace(/[\\"]/g, "\\$&").replace(/[\r\n]+/g, " ")}"`;
}

function toSubmittedAnswers(answers: Record<string, any>): Record<string, any> {
  const ordered: Record<string, any> = {};
  for (const id of [...ANSWER_ORDER, ...Object.keys(answers)]) {
    if (!(id in answers) || id in ordered) continue;
    const submittedName = SUBMITTED_BLOCK_NAMES[id];
    ordered[id] = submittedName
      ? { ...answers[id], blockName: submittedName }
      : answers[id];
  }
  return ordered;
}

function Screening() {
  const [ready, setReady] = useState(false);
  const [animationReady, setAnimationReady] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitInFlightRef = useRef(false);


  const { answers } = useFormAnswers();
  const currentBlock = useCurrentBlock();

  useEffect(() => {
    setReady(true);
    // Track form loaded event
    trackFormLoaded();
    void markFormOpened();
  }, []);

  const handleAnimationStart = () => {
    setAnimationReady(false);
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    animationTimeoutRef.current = setTimeout(() => {
      setAnimationReady(true);
    }, 500);
  };

  const hasTrackedWelcomeStartRef = useRef(false);

  useEffect(() => {
    handleAnimationStart();
    // Track block viewed when current block changes
    if (currentBlock) {
      const blockIndex = formBlocks.findIndex((b: any) => b.id === currentBlock.id);
      if (blockIndex !== -1) {
        trackBlockViewed(
          currentBlock.id,
          currentBlock.name || "unknown",
          blockIndex,
          formBlocks.length
        );

        // Track welcome start when user advances past the welcome block (block 0)
        // Welcome block has no answer, so onAnswerChange doesn't fire
        if (!hasTrackedWelcomeStartRef.current && blockIndex > 0) {
          hasTrackedWelcomeStartRef.current = true;
          trackWelcomeStartClicked();
          trackFormStarted();
        }
      }
    }
  }, [currentBlock]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!animationReady) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (!animationReady) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("click", handleClick, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("click", handleClick, true);
    };
  }, [animationReady]);

  const handleSubmit = async (
    data: any,
    {
      completeForm,
      setIsSubmitting,
      setSubmissionErr,
      goToBlock,
      setIsFieldValid,
      setFieldValidationErr,
    }: {
      completeForm: () => void;
      setIsSubmitting: (val: boolean) => void;
      setSubmissionErr: (value: string) => void;
      goToBlock: (id: string) => void;
      // Passed by renderer-core 5.9 but not part of the documented onSubmit API.
      setIsFieldValid?: (id: string, flag: boolean) => void;
      setFieldValidationErr?: (id: string, err: string) => void;
    }
  ) => {
    // Quill Forms' setSubmissionErr also clears isSubmitting, which re-enables
    // the Submit button mid-request, so it must only be used to report a failure.
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    console.debug("[DEBUG] Form submission started", {
      timestamp: new Date().toISOString(),
    });
    const rawAnswers = toSubmittedAnswers({
      ...(data?.answers || {}),
      ...HIDDEN_BLOCK_ANSWERS,
    });

    // Track submit button clicked
    trackFormSubmitClicked();

    // Track form submission started
    const answeredCount = Object.keys(rawAnswers).length;
    trackFormSubmissionStarted({
      answeredQuestions: answeredCount,
      totalQuestions: formBlocks.length + Object.keys(HIDDEN_BLOCK_ANSWERS).length,
    });

    // Basic validation
    const missingRequired = Object.entries(rawAnswers).some(
      ([blockId, a]: [string, any]) => {
        const isRequired = a?.required === true;
        const isMCQ =
          a?.blockType === "multipleChoice" || a?.blockType === "yesNo";
        const isBlank =
          !a?.value || (Array.isArray(a?.value) && a?.value.length === 0);
        return isRequired && isMCQ && isBlank;
      }
    );

    if (missingRequired) {
      alert("Please answer all required questions.");
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      return;
    }

    const emailValidation = validateEmail(rawAnswers.q6?.value);
    if (!emailValidation.isValid) {
      const message = emailValidation.errorMessage ?? VALIDATION_ERRORS.INVALID_EMAIL;
      submitInFlightRef.current = false;
      setSubmissionErr(message);
      goToBlock("q6");
      setIsFieldValid?.("q6", false);
      setFieldValidationErr?.("q6", message);
      return;
    }

    const transactionId = crypto.randomUUID();

    // 1. Send form data to the API via the parent window
    try {
      // Get parameters from SessionStorage as in parent.js
      const storage = sessionStorage.getItem("searchParams");
      const parsedUrlParams = new URLSearchParams(storage || "");

      // Get parentLocation from query parameters - now required
      const parentLocationParam = parsedUrlParams.get("parentLocation");
      if (!parentLocationParam) {
        throw new Error("parentLocation parameter is required but not provided");
      }

      const parentLocation = new URL(parentLocationParam);
      const PARENT_ORIGIN = parentLocation.origin;

      const response = await formSubmissionClient.submitFormToApi(
        window.parent,
        PARENT_ORIGIN,
        "formCompleted",
        {
          answers: rawAnswers,
          formId: transactionId, // Include formId as required by API
          id: transactionId, // Alternative formId field
        },
        transactionId,
      );

      console.log("Iframe: Form submission successful.", response);
      
      // Track successful submission
      trackFormSubmissionSuccess({
        transactionId,
      });
      
      setIsSubmitting(false);
      completeForm();
    } catch (error: any) {
      console.error("Iframe: Form submission failed.", error);
      
      // Track failed submission
      trackFormSubmissionFailed(error?.message || "Unknown error");
      
      submitInFlightRef.current = false;
      setSubmissionErr(
        "We couldn't submit your answers. Please check your connection and press Submit to try again."
      );
    }
  };

  if (!ready) {
    return null;
  }

  // Use refactored single validation function
  // The onBeforeNext callback fires when the user confirms an answer and advances,
  // giving us the definitive committed answer value for that block.
  const handleBeforeGoingNext = createBeforeGoingNextValidator({ q6: validateEmail, q7: validateBirthDate }, ({ currentBlockId, answers }) => {
    const blockIndex = formBlocks.findIndex((b: any) => b.id === currentBlockId);
    const blockDef = formBlocks[blockIndex];
    const blockType = blockDef?.name ?? "unknown";
    const blockKey = blockDef?.attributes?.label;
    const answerValue = answers?.[currentBlockId]?.value;
    const hasValue =
      answerValue !== undefined && answerValue !== null && answerValue !== "";

    console.log(`[RUM] 📝 Block confirmed — ${currentBlockId} ("${blockKey}"):`, answerValue);
    trackBlockAnswered(currentBlockId, blockType, hasValue, blockIndex, formBlocks.length);
  });

  const practiceName = getPracticeName();

  return (
    <div
      className={`flex flex-col items-center justify-between min-h-screen bg-gray-50 ${practiceName ? "has-practice-name" : ""}`}
      style={
        {
          backgroundImage: "url('/images/sleepbg1.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          ...(practiceName ? { "--practice-name": toCssString(practiceName) } : {}),
        } as CSSProperties
      }
    >
      <div className="flex items-center justify-center flex-1 w-full p-4 ">
        <div
          className="relative w-full max-w-2xl p-6 overflow-y-auto bg-white shadow-lg rounded-2xl"
          style={{
            height: "90vh",
          }}
        >
          <Form
            applyLogic={false}
            formId={1}
            beforeGoingNext={handleBeforeGoingNext}
            formObj={{
              blocks: formBlocks,
              messages: {
                "block.defaultThankYouScreen.label":
                  "View your results below and book your call",
              },
              settings: {
                animationDirection: "vertical",
                disableAutoSubmit: true,
                disableNavigationArrows: false,
                disableProgressBar: false,
                disableWheelSwiping: false,
                enableAutoSubmit: false,
                showLettersOnAnswers: false,
              },
              theme: {
                font: "Roboto",
                buttonsBgColor: "#522e90",
                logo: {
                  src: "./images/sax.png",
                },
                questionsColor: "#111827",
                answersColor: "#374151",
                buttonsFontColor: "#fff",
                buttonsBorderRadius: 8,
                errorsFontColor: "#b91c1c",
                errorsBgColor: "#fee2e2",
                progressBarFillColor: "#522e90",
                progressBarBgColor: "#e5e7eb",
              },
            }}
            onAnswerChange={(params: any) => {
              // Track the answered block and overall progress
              // Track the answered block and overall progress
              if (currentBlock) {
                const answersMap = (params || {}) as Record<string, { value: any }>;
                const currentAnswer = answersMap[currentBlock.id];
                const answerValue = currentAnswer?.value;
                const hasValue =
                  answerValue !== undefined &&
                  answerValue !== null &&
                  answerValue !== "";

                const blockDef = formBlocks.find((b: any) => b.id === currentBlock.id);
                const blockKey = blockDef?.attributes?.label;

                trackBlockAnswerChanged(
                  currentBlock.id,
                  currentBlock.name || "unknown",
                  hasValue,
                  blockKey
                );

                const answeredCount = Object.keys(answersMap).filter(
                  (k) =>
                    answersMap[k]?.value !== undefined &&
                    answersMap[k]?.value !== null &&
                    answersMap[k]?.value !== ""
                ).length;

                trackFormProgress(answeredCount, formBlocks.length);
              }
            }}
            // @ts-ignore
            onSubmit={(data, event) => handleSubmit(data, event)}
          />
          <LiveLogger blocks={formBlocks} />
        </div>
      </div>
    </div>
  );
}

export default Screening;
